// The Spotters — client-side printable Tax Invoice + Shipping Label.
// The invoice is a GST-compliant tax invoice: seller of record is the Vendor,
// the marketplace (Divyantik India Pvt Ltd) is shown as the facilitating ECO,
// tax splits into CGST+SGST (intra-state) or IGST (inter-state). The payload is
// assembled server-side (/orders/{id}/invoice) and rendered into a print window.
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const esc = (s) => String(s == null ? "" : s).replace(/[&<>"]/g, (c) => (
  { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]
));
const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const shortId = (id) => String(id || "").slice(0, 8).toUpperCase();
const orderRef = (o) => o.order_no || ("TS-" + shortId(o.id));

const SHELL = (title, body) => `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>
  @page { size: A4 portrait; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #111; margin: 0; padding: 24px; }
  @media print { body { padding: 0; } #inv { font-size: 11px; } #inv table.items tbody td { padding: 9px 6px; } }
  .mono { font-family: "JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace; }
  .row { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
  .brand { font-weight: 800; font-size: 22px; letter-spacing: -.02em; }
  .tag { font-size: 10px; letter-spacing: .18em; text-transform: uppercase; color: #666; }
  h1 { font-size: 15px; letter-spacing: .18em; text-transform: uppercase; margin: 0; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; }
  th, td { text-align: left; padding: 8px 10px; border-bottom: 1px solid #ddd; font-size: 13px; }
  th { font-size: 10px; letter-spacing: .12em; text-transform: uppercase; color: #666; border-bottom: 1.5px solid #111; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .box { border: 1.5px solid #111; padding: 14px; }
  .muted { color: #666; font-size: 12px; }
  .totrow td { border: 0; padding: 3px 10px; }
  .grand td { border-top: 1.5px solid #111; font-weight: 700; }
  .metaline { display: flex; flex-wrap: wrap; gap: 6px 24px; margin-top: 14px; padding: 8px 0; border-top: 1px solid #ddd; border-bottom: 1px solid #ddd; font-size: 12px; }
  .facil { margin-top: 10px; font-size: 11px; color: #555; line-height: 1.5; }
  .print-hint { margin-top: 24px; text-align: center; }
  .print-hint button { font: inherit; padding: 10px 18px; border: 1.5px solid #111; background: #111; color: #fff; letter-spacing: .1em; text-transform: uppercase; font-size: 12px; cursor: pointer; }
  @media print { .print-hint { display: none; } body { padding: 0; } }
</style></head><body>${body}
<div class="print-hint"><button onclick="window.print()">Print / Save as PDF</button></div>
<script>window.onload=function(){setTimeout(function(){window.print();},350);};</script>
</body></html>`;

function openDoc(title, html) {
  const w = window.open("", "_blank");
  if (!w) { alert("Please allow pop-ups to download the document."); return; }
  w.document.open();
  w.document.write(SHELL(title, html));
  w.document.close();
}

const INVOICE_CSS = `
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=Manrope:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap">
<style>
#inv{--ink:#1c1814;--soft:#4a443c;--muted:#8a8377;--accent:#b1462c;--line:#e7e0d3;--band:#faf7f0;--ok:#2f7d43;
  font-family:"Manrope",system-ui,sans-serif;color:var(--ink);font-size:12.5px}
#inv .mono{font-family:"JetBrains Mono",ui-monospace,monospace}
#inv .lbl{font-family:"JetBrains Mono",monospace;font-size:9.5px;letter-spacing:.16em;text-transform:uppercase;color:var(--accent)}
#inv .num{text-align:right;font-variant-numeric:tabular-nums}
#inv .rule{height:1px;background:var(--line);margin:22px 0}
#inv .top{display:flex;justify-content:space-between;align-items:flex-start;gap:24px}
#inv .wordmark{font-family:"Playfair Display",Georgia,serif;font-weight:900;font-size:32px;letter-spacing:-.02em;line-height:1}
#inv .sub{font-family:"JetBrains Mono",monospace;font-size:9.5px;letter-spacing:.14em;text-transform:uppercase;color:var(--muted);margin-top:7px}
#inv .tagline{font-family:"Playfair Display",serif;font-size:14px;color:var(--soft);margin-top:9px}
#inv .title{font-family:"Playfair Display",serif;font-weight:900;font-size:21px;letter-spacing:.02em;color:var(--accent);text-transform:uppercase;text-align:right}
#inv .metagrid{margin-top:12px;display:grid;grid-template-columns:auto auto;gap:5px 16px;font-size:12px;text-align:right}
#inv .metagrid .k{color:var(--muted);text-align:left}
#inv .metagrid b{font-weight:600}
#inv .pending{color:var(--accent)}
#inv .parties{display:grid;grid-template-columns:1.1fr 1fr 1fr;gap:24px;margin-top:22px}
#inv .party .nm{font-weight:700;font-size:13.5px}
#inv .party .r{font-size:11.5px;color:var(--soft);line-height:1.5;margin-top:2px}
#inv .party .gst{font-family:"JetBrains Mono",monospace;font-size:10.5px;margin-top:5px}
#inv table.items{width:100%;border-collapse:collapse;margin-top:22px}
#inv table.items thead th{font-family:"JetBrains Mono",monospace;font-size:8px;letter-spacing:.06em;text-transform:uppercase;color:var(--muted);text-align:left;padding:0 8px 7px;border-bottom:1.5px solid var(--ink);font-weight:500}
#inv table.items thead th.num{text-align:right}
#inv table.items tbody td{padding:12px 8px;font-size:12px;border-bottom:1px solid var(--line);vertical-align:top}
#inv .pname{font-weight:600}
#inv .psize{font-size:10.5px;color:var(--muted);margin-top:2px}
#inv .midgrid{display:grid;grid-template-columns:1fr 1fr;gap:30px;margin-top:24px}
#inv table.tsum{width:100%;border-collapse:collapse}
#inv table.tsum th{font-family:"JetBrains Mono",monospace;font-size:8.5px;letter-spacing:.06em;text-transform:uppercase;color:var(--muted);text-align:left;padding:6px 8px;border-bottom:1px solid var(--line);font-weight:500}
#inv table.tsum th.num{text-align:right}
#inv table.tsum td{padding:7px 8px;font-size:11.5px;border-bottom:1px solid var(--line)}
#inv table.tsum tr:last-child td{border-bottom:0;font-weight:700;background:var(--band)}
#inv .totals .line{display:flex;justify-content:space-between;padding:6px 0;font-size:12.5px;color:var(--soft)}
#inv .totals .line b{color:var(--ink);font-weight:600}
#inv .totals .grand{display:flex;justify-content:space-between;align-items:baseline;margin-top:6px;padding-top:11px;border-top:2px solid var(--ink)}
#inv .totals .grand span{font-family:"JetBrains Mono",monospace;font-size:11px;letter-spacing:.1em;text-transform:uppercase;color:var(--muted)}
#inv .totals .grand .amt{font-family:"Playfair Display",serif;font-weight:900;font-size:26px;color:var(--accent)}
#inv .totals .save{margin-top:8px;text-align:right;font-family:"JetBrains Mono",monospace;font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:var(--ok)}
#inv .posrow{display:grid;grid-template-columns:1fr 1fr;gap:30px;margin-top:18px;align-items:start}
#inv .pos div{display:flex;gap:8px;font-size:11.5px;padding:3px 0}
#inv .pos .k{color:var(--muted);min-width:118px}
#inv .words{background:var(--band);border:1px solid var(--line);padding:12px 14px}
#inv .words .t{font-size:14px;font-family:"Playfair Display",serif}
#inv .cards{display:grid;grid-template-columns:1fr 1fr;gap:18px;margin-top:22px}
#inv .card{border:1px solid var(--line);border-radius:9px;padding:14px 16px}
#inv .card h4{margin:0 0 10px}
#inv .drow{display:flex;font-size:11.5px;padding:3px 0}
#inv .drow .k{color:var(--muted);min-width:140px}
#inv .pill{font-family:"JetBrains Mono",monospace;font-size:8.5px;letter-spacing:.1em;text-transform:uppercase;color:var(--ok);border:1px solid var(--ok);border-radius:3px;padding:1px 7px}
#inv .facil{margin-top:22px;padding-top:14px;border-top:1px solid var(--line);font-size:10.5px;color:var(--muted);line-height:1.55}
#inv .signrow{margin-top:22px;display:flex;justify-content:space-between;align-items:flex-end;gap:24px}
#inv .signrow .bc{align-self:flex-end}
#inv .sbox{min-width:230px;text-align:right}
#inv .sbox .sig{max-height:58px;max-width:220px;object-fit:contain;display:block;margin:2px 0 4px auto}
#inv .sbox .ln{height:40px;border-bottom:1px solid var(--muted)}
@media print{ #inv .card,#inv .badges{ -webkit-print-color-adjust:exact;print-color-adjust:exact } }
</style>`;

// Code 128 (subset B) barcode as inline SVG — no external library, so it renders
// in the print window and PDF. Used for the Order ID (hub / returns scanning).
const _C128 = ["212222","222122","222221","121223","121322","131222","122213","122312","132212","221213","221312","231212","112232","122132","122231","113222","123122","123221","223211","221132","221231","213212","223112","312131","311222","321122","321221","312212","322112","322211","212123","212321","232121","111323","131123","131321","112313","132113","132311","211313","231113","231311","112133","112331","132131","113123","113321","133121","313121","211331","231131","213113","213311","213131","311123","311321","331121","312113","312311","332111","314111","221411","431111","111224","111422","121124","121421","141122","141221","112214","112412","122114","122411","142112","142211","241211","221114","413111","241112","134111","111242","121142","121241","114212","124112","124211","411212","421112","421211","212141","214121","412121","111143","111341","131141","114113","114311","411113","411311","113141","114131","311141","411131","211412","211214","211232","233111","2331112"];
function barcode128(data) {
  const s = String(data || "");
  const codes = [104]; // Start B
  let sum = 104;
  for (let i = 0; i < s.length; i++) { const v = s.charCodeAt(i) - 32; codes.push(v); sum += v * (i + 1); }
  codes.push(sum % 103); // checksum
  codes.push(106);       // Stop
  const mw = 1.35, h = 40, quiet = 10;
  let x = quiet * mw, bars = "";
  for (const c of codes) {
    const pat = _C128[c]; let bar = true;
    for (const ch of pat) { const w = parseInt(ch, 10) * mw; if (bar) bars += `<rect x="${x.toFixed(2)}" y="0" width="${w.toFixed(2)}" height="${h}"/>`; x += w; bar = !bar; }
  }
  const total = x + quiet * mw;
  return `<svg width="${total.toFixed(0)}" height="${h + 15}" viewBox="0 0 ${total.toFixed(0)} ${h + 15}" xmlns="http://www.w3.org/2000/svg"><rect width="100%" height="100%" fill="#fff"/><g fill="#111">${bars}</g><text x="${(total / 2).toFixed(0)}" y="${h + 12}" text-anchor="middle" font-family="monospace" font-size="11" letter-spacing="1">${esc(s)}</text></svg>`;
}

// Renders the redesigned GST tax invoice from the server-assembled payload.
function invoiceBody(inv) {
  const inter = inv.inter_state;
  const S = inv.seller || {}, B = inv.buyer || {}, M = inv.marketplace || {}, T = inv.totals || {};
  const P = inv.payment || {}, F = inv.fulfilment || {};
  const invNo = inv.invoice_pending
    ? `<b class="pending">Pending${inv.invoice_mode === "manual" ? " — set number" : ""}</b>`
    : `<b class="mono">${esc(inv.invoice_no || "—")}</b>`;

  const taxHead = inter
    ? `<th class="num">IGST</th>`
    : `<th class="num">CGST</th><th class="num">SGST</th>`;
  const rows = (inv.lines || []).map((l) => {
    const taxCells = inter
      ? `<td class="num">${money(l.igst)}</td>`
      : `<td class="num">${money(l.cgst)}</td><td class="num">${money(l.sgst)}</td>`;
    return `<tr>
      <td>${l.sno}</td>
      <td><div class="pname">${esc(l.title)}</div>${l.size ? `<div class="psize">Size ${esc(l.size)}</div>` : ""}</td>
      <td class="mono">${esc(l.hsn || "—")}</td>
      <td class="num">${l.qty}</td>
      <td class="num">${money(l.mrp && l.mrp >= l.unit_price ? l.mrp : l.unit_price)}</td>
      <td class="num">${l.discount ? money(l.discount) : "—"}</td>
      <td class="num">${money(l.taxable)}</td>
      <td class="num">${Number(l.gst_rate).toFixed(0)}%</td>
      ${taxCells}
      <td class="num">${money(l.amount)}</td>
    </tr>`;
  }).join("");

  const tsumHead = inter
    ? `<th class="num">IGST</th>`
    : `<th class="num">CGST</th><th class="num">SGST</th>`;
  const tsumCells = inter
    ? `<td class="num">${money(T.igst)}</td>`
    : `<td class="num">${money(T.cgst)}</td><td class="num">${money(T.sgst)}</td>`;
  const totalTaxLine = inter
    ? `<div class="line"><span>IGST</span><b>${money(T.igst)}</b></div>`
    : `<div class="line"><span>CGST + SGST</span><b>${money((T.cgst || 0) + (T.sgst || 0))}</b></div>`;

  const drow = (k, v) => v ? `<div class="drow"><span class="k">${k}</span><span>${v}</span></div>` : "";

  return `<div id="inv">${INVOICE_CSS}
    <div class="top">
      <div>
        <div class="wordmark">TheSpotters</div>
        <div class="sub">/ Certified · № TS-2026</div>
        <div class="tagline">Every product. Physically inspected.</div>
      </div>
      <div>
        <div class="title">Tax Invoice</div>
        <div class="metagrid">
          <span class="k">Invoice No.</span>${invNo}
          <span class="k">Invoice Date</span><b>${esc(inv.invoice_date || "—")}</b>
          <span class="k">Order No.</span><b class="mono">${esc(inv.order_no)}</b>
          <span class="k">Order Date</span><b>${esc(inv.order_date)}</b>
        </div>
      </div>
    </div>
    <div class="rule"></div>
    <div class="parties">
      <div class="party">
        <div class="lbl">Sold by (Supplier)</div>
        <div class="nm">${esc(S.name)}</div>
        <div class="r">${esc(S.address || "—")}</div>
        <div class="gst">GSTIN&nbsp; ${esc(S.gstin || "—")}</div>
        <div class="gst">PAN&nbsp;&nbsp;&nbsp;&nbsp; ${esc(S.pan || "—")}${S.state_code ? ` · State ${esc(S.state_code)}` : ""}</div>
      </div>
      <div class="party">
        <div class="lbl">Bill to</div>
        <div class="nm">${esc(B.name)}</div>
        <div class="r">${esc(B.address || "—")}</div>
        <div class="gst">${esc(B.phone || "")}</div>
      </div>
      <div class="party">
        <div class="lbl">Ship to</div>
        <div class="nm">${esc(B.name)}</div>
        <div class="r">${esc(B.address || "—")}</div>
        <div class="gst">${esc(B.phone || "")}</div>
      </div>
    </div>
    <table class="items">
      <thead><tr>
        <th>#</th><th>Product</th><th>HSN</th><th class="num">Qty</th>
        <th class="num">MRP ₹</th><th class="num">Disc ₹</th><th class="num">Taxable ₹</th><th class="num">GST</th>${taxHead}<th class="num">Amount ₹</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="midgrid">
      <div>
        <div class="lbl" style="margin-bottom:8px">Tax Summary</div>
        <table class="tsum">
          <thead><tr><th>Taxable ₹</th>${tsumHead}<th class="num">Total Tax ₹</th></tr></thead>
          <tbody>
            <tr><td>${money(T.taxable)}</td>${tsumCells}<td class="num">${money((T.cgst || 0) + (T.sgst || 0) + (T.igst || 0))}</td></tr>
          </tbody>
        </table>
      </div>
      <div class="totals">
        <div class="line"><span>Taxable value</span><b>${money(T.taxable)}</b></div>
        ${totalTaxLine}
        <div class="line"><span>Shipping</span><b>${T.shipping ? money(T.shipping) : "Free"}</b></div>
        <div class="grand"><span>Total Payable</span><span class="amt">${money(T.grand_total)}</span></div>
        ${T.discount ? `<div class="save">You saved ${money(T.discount)} vs MRP</div>` : ""}
      </div>
    </div>
    <div class="posrow">
      <div class="pos">
        <div><span class="k">Place of supply</span><b>${esc(inv.place_of_supply || "—")}</b></div>
        <div><span class="k">Supply type</span><b>${inter ? "Inter-state (IGST)" : "Intra-state (CGST + SGST)"}</b></div>
        <div><span class="k">Reverse charge</span><b>${esc(inv.reverse_charge || "No")}</b></div>
      </div>
      <div class="words">
        <div class="lbl" style="margin-bottom:3px">Amount in words</div>
        <div class="t">${esc(inv.amount_in_words)}</div>
      </div>
    </div>
    <div class="cards">
      <div class="card">
        <div class="lbl" style="margin-bottom:10px">Payment Details</div>
        <div class="drow"><span class="k">Status</span>${P.status === "Paid" ? '<span class="pill">Paid</span>' : `<span>${esc(P.status || "—")}</span>`}</div>
        ${drow("Method", esc(P.method))}
        ${drow("Transaction ref", P.reference ? `<span class="mono">${esc(P.reference)}</span>` : "")}
        ${drow("Paid on", esc(P.date))}
      </div>
      <div class="card">
        <div class="lbl" style="margin-bottom:10px">Fulfilment Details</div>
        ${drow("Type", esc(F.type))}
        ${drow("Sold &amp; shipped by", esc(F.sold_shipped_by))}
        ${drow("Courier", esc(F.courier))}
        ${drow("AWB / Tracking", F.awb ? `<span class="mono">${esc(F.awb)}</span>` : "")}
        ${drow("Shipped on", esc(F.shipped_on))}
        ${drow("Delivered on", esc(F.delivered_on))}
      </div>
    </div>
    <div class="signrow">
      <div class="bc">${barcode128(inv.order_no)}</div>
      <div class="sbox">
        <div class="lbl" style="margin-bottom:2px">For ${esc(S.name)}</div>
        ${S.signature ? `<img class="sig" src="${S.signature}" alt="Signature">` : `<div class="ln"></div>`}
        <div class="sub">Authorised signatory</div>
      </div>
    </div>
    <div class="facil">Sold by <b>${esc(S.name)}</b> through The Spotters, a marketplace operated by
      ${esc(M.legal_name)} (GSTIN ${esc(M.gstin)}; CIN ${esc(M.cin)}), acting as an E-Commerce Operator that
      collects tax at source (TCS) under GST. Registered office: ${esc(M.address)}. This is a computer-generated
      invoice and does not require a physical signature.</div>
  </div>`;
}

export async function printInvoice(order) {
  try {
    const { data } = await api.get(`/orders/${order.id}/invoice`);
    if (data.invoice_pending && data.invoice_mode === "manual") {
      toast.message("Set this order's invoice number first (Invoicing is on manual mode).");
    }
    openDoc(`Invoice ${esc(data.invoice_no || data.order_no)}`, invoiceBody(data));
  } catch (e) {
    toast.error(formatApiError(e.response?.data?.detail) || "Couldn't generate the invoice");
  }
}

export function printLabel(order, vendorName) {
  const addr = order.address || {};
  const items = order.items || [];
  const seller = vendorName || items[0]?.vendor_slug || "The Spotters Vendor";
  const qty = items.reduce((s, it) => s + Number(it.quantity || 1), 0);
  const awb = order.awb || "PENDING";
  const contents = items.map((it) => `${esc(it.title)} x${esc(it.quantity)}`).join(" · ");
  const body = `
    <div class="box" style="border-width:2.5px">
      <div class="row">
        <div class="brand">TheSpotters</div>
        <div style="text-align:right"><div class="tag">Prepaid</div><div class="mono" style="font-size:12px">${esc(orderRef(order))}</div></div>
      </div>
      <div style="border-top:1.5px solid #111;margin:12px 0"></div>
      <div class="row">
        <div style="flex:1">
          <div class="tag">From</div>
          <div style="margin-top:4px"><b>${esc(seller)}</b></div>
          <div class="muted">c/o The Spotters Hub</div>
        </div>
        <div style="flex:1.4">
          <div class="tag">Deliver to</div>
          <div style="margin-top:4px;font-size:18px"><b>${esc(addr.full_name || order.buyer_name || "Customer")}</b></div>
          <div>${esc(addr.line1 || "")}${addr.line2 ? ", " + esc(addr.line2) : ""}</div>
          <div>${esc(addr.city || "")}, ${esc(addr.state || "")}</div>
          <div style="font-size:22px;font-weight:800;letter-spacing:.04em" class="mono">${esc(addr.pincode || "")}</div>
          <div class="mono">${esc(addr.phone || "")}</div>
        </div>
      </div>
      <div style="border-top:1.5px solid #111;margin:12px 0"></div>
      <div class="row">
        <div><div class="tag">Courier</div><div>${esc(order.courier_name || "Assigned at pickup")}</div></div>
        <div><div class="tag">Weight</div><div>${(Math.max(0.3, 0.3 * qty)).toFixed(1)} kg</div></div>
        <div><div class="tag">Items</div><div>${qty}</div></div>
      </div>
      <div style="margin-top:14px">
        <div class="tag">AWB</div>
        <div class="mono" style="font-size:26px;font-weight:800;letter-spacing:.08em">${esc(awb)}</div>
        <div style="height:44px;margin-top:6px;background:repeating-linear-gradient(90deg,#111 0 2px,#fff 2px 4px,#111 4px 7px,#fff 7px 9px)"></div>
      </div>
      <div class="muted" style="margin-top:12px">Contents: ${contents || "—"}</div>
    </div>`;
  openDoc(`Label ${orderRef(order)}`, body);
}
